import { useState, useEffect, useCallback } from 'react';
import { 
  X, Shield, AlertTriangle, CheckCircle, Clock, Phone, Mail,
  Eye, EyeOff, FileText, MessageSquare, MapPin, Building2,
  Globe, Star, DollarSign, Users, RefreshCw, ChevronDown,
  ChevronUp, ExternalLink, Calendar, Sparkles, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configs
const ADMIN_STATUS_CONFIG = {
  registered: { label: 'Registrato', color: 'bg-gray-100 text-gray-700' },
  docs_pending: { label: 'Docs Mancanti', color: 'bg-amber-100 text-amber-700' },
  under_review: { label: 'In Revisione', color: 'bg-blue-100 text-blue-700' },
  contact_pending: { label: 'Da Contattare', color: 'bg-purple-100 text-purple-700' },
  pre_verified: { label: 'Pre-Verificato', color: 'bg-cyan-100 text-cyan-700' },
  approved: { label: 'Approvato', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700' },
  suspended: { label: 'Sospeso', color: 'bg-orange-100 text-orange-700' }
};

const SOURCE_LABELS = {
  provider_portal: { label: 'Registrazione Autonoma', icon: '🌐', desc: 'Il fornitore si è registrato dal portale' },
  admin_manual: { label: 'Inserito da Admin', icon: '👤', desc: 'Aggiunto manualmente via Quick Add' },
  import_curated: { label: 'Import Curato', icon: '📥', desc: 'Importato da lista verificata' },
  google_places: { label: 'Google Places', icon: '📍', desc: 'Importato da Google Places' }
};

const ONBOARDING_LABELS = {
  chat: { label: 'Chat Guidata', icon: '💬', desc: 'Dati estratti automaticamente dalla conversazione', color: 'bg-indigo-50 border-indigo-200' },
  form: { label: 'Form Classico', icon: '📝', desc: 'Compilato manualmente dal fornitore', color: 'bg-slate-50 border-slate-200' },
  skipped: { label: 'Saltato', icon: '⏭️', desc: 'Onboarding non completato', color: 'bg-gray-50 border-gray-200' }
};

const NOTE_TYPES = [
  { value: 'contact', label: 'Contatto', icon: '📞', color: 'bg-purple-100 text-purple-700' },
  { value: 'warning', label: 'Warning', icon: '⚠️', color: 'bg-red-100 text-red-700' },
  { value: 'info', label: 'Info', icon: 'ℹ️', color: 'bg-blue-100 text-blue-700' },
  { value: 'commercial', label: 'Commerciale', icon: '💼', color: 'bg-green-100 text-green-700' },
  { value: 'approval', label: 'Approvazione', icon: '✅', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'general', label: 'Generale', icon: '📝', color: 'bg-gray-100 text-gray-700' }
];

/**
 * ProviderDetail - Scheda dettaglio provider migliorata
 * 
 * Features:
 * - Warning prominente per categorie sensibili
 * - Distinzione fonte dati (chat/form/import)
 * - Campi estratti vs campi mancanti
 * - Note come mini-CRM con categorizzazione
 * - Azioni admin contestuali
 */
export function ProviderDetail({ providerId, onClose, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Note input
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('info');
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    source: true,
    extracted: true,
    documents: true,
    notes: true,
    actions: true
  });

  const loadDetail = useCallback(async () => {
    if (!providerId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/${providerId}/detail`);
      if (!res.ok) throw new Error('Failed to load detail');
      const detail = await res.json();
      setData(detail);
    } catch (err) {
      console.error('Failed to load detail:', err);
      toast.error('Errore caricamento dettagli');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Actions
  const changeStatus = async (newStatus) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/${providerId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_status: newStatus, changed_by: 'admin' })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Stato aggiornato: ${ADMIN_STATUS_CONFIG[newStatus]?.label}`);
      loadDetail();
      onUpdate?.();
    } catch (err) {
      toast.error('Errore cambio stato');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleVisibility = async () => {
    const newValue = !data.profile.visible_to_chiara;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/${providerId}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible_to_chiara: newValue, changed_by: 'admin' })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(newValue ? 'Ora visibile a Chiara' : 'Nascosto da Chiara');
      loadDetail();
      onUpdate?.();
    } catch (err) {
      toast.error('Errore cambio visibilità');
    } finally {
      setActionLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/${providerId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote, note_type: noteType, author: 'admin' })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Nota aggiunta');
      setNewNote('');
      loadDetail();
    } catch (err) {
      toast.error('Errore aggiunta nota');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl p-8">
          <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto" />
          <p className="text-gray-500 mt-3">Caricamento...</p>
        </div>
      </div>
    );
  }

  const { profile, policy, notes, documents, docs_status, missing_docs, user } = data;
  const isSensitive = profile.sensitive_category || profile.risk_level === 'high';
  const sourceInfo = SOURCE_LABELS[profile.source] || SOURCE_LABELS.provider_portal;
  const onboardingInfo = ONBOARDING_LABELS[profile.onboarding_method] || ONBOARDING_LABELS.form;

  // Campi estratti (da onboarding) vs campi base
  const extractedFields = {
    populated: [],
    missing: []
  };
  
  const fieldsToCheck = [
    { key: 'business_name', label: 'Nome attività' },
    { key: 'type', label: 'Tipo' },
    { key: 'subtype', label: 'Sottotipo' },
    { key: 'comune', label: 'Comune' },
    { key: 'macro_area', label: 'Macro Area' },
    { key: 'short_description', label: 'Descrizione breve' },
    { key: 'long_description', label: 'Descrizione lunga' },
    { key: 'services_offered', label: 'Servizi offerti' },
    { key: 'good_for', label: 'Ideale per' },
    { key: 'price_band', label: 'Fascia prezzo' },
    { key: 'languages_spoken', label: 'Lingue' },
    { key: 'contact_email', label: 'Email' },
    { key: 'contact_phone', label: 'Telefono' },
    { key: 'capacity_info', label: 'Capacità (camere/posti)' },
    { key: 'special_services', label: 'Servizi speciali' }
  ];
  
  fieldsToCheck.forEach(field => {
    const value = profile[field.key];
    const hasValue = value && (Array.isArray(value) ? value.length > 0 : true);
    if (hasValue) {
      extractedFields.populated.push({ ...field, value });
    } else {
      extractedFields.missing.push(field);
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-4 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-umbria-charcoal flex items-center gap-2">
                {profile.business_name}
                {profile.visible_to_chiara && <Eye className="h-5 w-5 text-green-500" />}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {profile.type} • {profile.comune || profile.macro_area || 'Umbria'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          
          {/* ========== SENSITIVE WARNING ========== */}
          {isSensitive && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl" data-testid="sensitive-warning">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Shield className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    CATEGORIA SENSIBILE
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    Questa categoria ({profile.type}) richiede verifica approfondita prima dell'approvazione.
                  </p>
                  <div className="mt-3 space-y-2 text-sm">
                    {policy?.requires_human_contact && (
                      <div className="flex items-center gap-2 text-red-700">
                        <Phone className="h-4 w-4" />
                        <strong>Contatto telefonico obbligatorio</strong>
                      </div>
                    )}
                    {policy?.background_check_off_platform && (
                      <div className="flex items-center gap-2 text-red-700">
                        <Shield className="h-4 w-4" />
                        <strong>Background check OFF-PLATFORM richiesto</strong>
                      </div>
                    )}
                    {missing_docs?.length > 0 && (
                      <div className="flex items-center gap-2 text-red-700">
                        <FileText className="h-4 w-4" />
                        <strong>Documenti mancanti: {missing_docs.join(', ')}</strong>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800">
                    ⚠️ NON approvare senza aver completato tutte le verifiche. 
                    Visibilità a Chiara bloccata fino ad approvazione completa.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== SOURCE & ONBOARDING INFO ========== */}
          <Card className={onboardingInfo.color}>
            <CardHeader className="py-3 cursor-pointer" onClick={() => toggleSection('source')}>
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Fonte Dati e Onboarding
                </span>
                {expandedSections.source ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.source && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-4">
                  {/* Source */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{sourceInfo.icon}</span>
                      <span className="font-medium text-sm">{sourceInfo.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{sourceInfo.desc}</p>
                  </div>
                  
                  {/* Onboarding Method (solo per provider_portal) */}
                  {profile.source === 'provider_portal' && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{onboardingInfo.icon}</span>
                        <span className="font-medium text-sm">{onboardingInfo.label}</span>
                      </div>
                      <p className="text-xs text-gray-500">{onboardingInfo.desc}</p>
                    </div>
                  )}
                </div>
                
                {/* User Info */}
                {user && (
                  <div className="mt-3 p-3 bg-white rounded-lg border">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Account Fornitore
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Email:</span>{' '}
                        <span className="font-medium">{user.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Verificata:</span>{' '}
                        {user.is_verified ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Sì
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ========== EXTRACTED DATA (from onboarding) ========== */}
          <Card>
            <CardHeader className="py-3 cursor-pointer" onClick={() => toggleSection('extracted')}>
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Dati Raccolti
                  <Badge variant="secondary">{extractedFields.populated.length}/{fieldsToCheck.length}</Badge>
                </span>
                {expandedSections.extracted ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.extracted && (
              <CardContent className="pt-0">
                {/* Populated Fields */}
                <div className="space-y-2 mb-4">
                  {extractedFields.populated.map((field) => (
                    <div key={field.key} className="flex items-start justify-between p-2 bg-green-50 rounded border border-green-100">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500">{field.label}</span>
                        <p className="text-sm font-medium text-umbria-charcoal">
                          {Array.isArray(field.value) 
                            ? field.value.join(', ') 
                            : typeof field.value === 'object'
                              ? JSON.stringify(field.value)
                              : field.value
                          }
                        </p>
                      </div>
                      <CheckCircle className="h-4 w-4 text-green-600 mt-1" />
                    </div>
                  ))}
                </div>
                
                {/* Missing Fields */}
                {extractedFields.missing.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Campi mancanti ({extractedFields.missing.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {extractedFields.missing.map((field) => (
                        <Badge key={field.key} className="bg-amber-50 text-amber-700 border border-amber-200">
                          {field.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ========== DOCUMENTS ========== */}
          <Card>
            <CardHeader className="py-3 cursor-pointer" onClick={() => toggleSection('documents')}>
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Documenti
                  {missing_docs?.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-700">{missing_docs.length} mancanti</Badge>
                  )}
                </span>
                {expandedSections.documents ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.documents && (
              <CardContent className="pt-0">
                {/* Required Docs Status */}
                <div className="space-y-2">
                  {Object.entries(docs_status || {}).map(([doc, uploaded]) => (
                    <div key={doc} className={`flex items-center justify-between p-2 rounded border ${
                      uploaded ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
                    }`}>
                      <span className="text-sm">{doc.replace(/_/g, ' ')}</span>
                      {uploaded ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Caricato
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Mancante
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Uploaded Documents */}
                {documents?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">Documenti caricati</h4>
                    <div className="space-y-2">
                      {documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{doc.doc_type}</p>
                            <p className="text-xs text-gray-500">{doc.filename}</p>
                          </div>
                          <Badge className={doc.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {doc.verified ? 'Verificato' : 'Da verificare'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ========== NOTES (Mini-CRM) ========== */}
          <Card>
            <CardHeader className="py-3 cursor-pointer" onClick={() => toggleSection('notes')}>
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Note Interne
                  <Badge variant="secondary">{notes?.length || 0}</Badge>
                </span>
                {expandedSections.notes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </CardTitle>
            </CardHeader>
            {expandedSections.notes && (
              <CardContent className="pt-0">
                {/* Add Note */}
                <div className="mb-4 space-y-2">
                  <div className="flex gap-2">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NOTE_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1 min-h-[60px]"
                    />
                  </div>
                  <Button 
                    onClick={addNote} 
                    disabled={!newNote.trim() || actionLoading}
                    size="sm"
                  >
                    Aggiungi Nota
                  </Button>
                </div>
                
                {/* Notes List */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {(notes || []).map((note, idx) => {
                    const typeConfig = NOTE_TYPES.find(t => t.value === note.note_type) || NOTE_TYPES[5];
                    return (
                      <div key={idx} className={`p-3 rounded-lg border ${typeConfig.color}`}>
                        <div className="flex items-start justify-between mb-1">
                          <Badge className={typeConfig.color}>
                            {typeConfig.icon} {typeConfig.label}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(note.created_at).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-sm">{note.content}</p>
                        {note.author && (
                          <p className="text-xs text-gray-500 mt-1">— {note.author}</p>
                        )}
                      </div>
                    );
                  })}
                  {(!notes || notes.length === 0) && (
                    <p className="text-center text-gray-500 py-4">Nessuna nota</p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* ========== ACTIONS FOOTER ========== */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4">
          <div className="flex flex-wrap gap-2">
            {/* Status Actions */}
            {profile.admin_status !== 'approved' && (
              <>
                {profile.admin_status === 'registered' && (
                  <Button 
                    onClick={() => changeStatus('under_review')} 
                    disabled={actionLoading}
                    variant="outline"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    In Revisione
                  </Button>
                )}
                
                {profile.admin_status === 'under_review' && (
                  <>
                    <Button 
                      onClick={() => changeStatus('contact_pending')} 
                      disabled={actionLoading}
                      variant="outline"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Da Contattare
                    </Button>
                    
                    {!isSensitive && missing_docs?.length === 0 && (
                      <Button 
                        onClick={() => changeStatus('approved')} 
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approva
                      </Button>
                    )}
                  </>
                )}
                
                {profile.admin_status === 'contact_pending' && (
                  <Button 
                    onClick={() => changeStatus('approved')} 
                    disabled={actionLoading || (isSensitive && missing_docs?.length > 0)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approva
                  </Button>
                )}
                
                <Button 
                  onClick={() => changeStatus('rejected')} 
                  disabled={actionLoading}
                  variant="destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Rifiuta
                </Button>
              </>
            )}
            
            {profile.admin_status === 'approved' && (
              <Button 
                onClick={() => changeStatus('suspended')} 
                disabled={actionLoading}
                variant="outline"
                className="text-orange-600 border-orange-300"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Sospendi
              </Button>
            )}
            
            {/* Visibility Toggle */}
            <Button
              onClick={toggleVisibility}
              disabled={actionLoading || (isSensitive && profile.admin_status !== 'approved')}
              variant={profile.visible_to_chiara ? "outline" : "default"}
              className={profile.visible_to_chiara ? "text-green-600 border-green-300" : ""}
            >
              {profile.visible_to_chiara ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Nascondi da Chiara
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Rendi Visibile
                </>
              )}
            </Button>
          </div>
          
          {/* Sensitive Warning in Footer */}
          {isSensitive && profile.admin_status !== 'approved' && (
            <p className="text-xs text-red-600 mt-2">
              ⚠️ Categoria sensibile: verifica completa obbligatoria prima dell'approvazione
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
